export enum PulseType {
  SYSTEM = 'SYSTEM',
  SECURITY = 'SECURITY',
  GOVERNANCE = 'GOVERNANCE',
  FINANCIAL = 'FINANCIAL',
  GENERAL = 'GENERAL'
}

export enum PulseSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

export type ChannelType = 'GROUP' | 'DIRECT' | 'ANNOUNCEMENT';

export interface Channel {
  id: string;
  nodeId: string;
  name: string;
  channelType: ChannelType;
  allowedRoles: string[];
  isPrivate: boolean;
  unreadCount: number;
}

export interface Message {
  id: string;
  channelId: string;
  senderUserId: string;
  senderName: string;
  senderAvatarColor: string;
  body: string;
  createdAt: string;
  editedAt?: string;
  isEdited?: boolean;
  isPinned: boolean;
  replyToId?: string;
  isPending?: boolean;
}

export type OrgEventStatus = 'SCHEDULED' | 'CANCELLED';
export type RsvpResponse = 'YES' | 'NO' | 'MAYBE' | 'NONE';

export interface OrgEvent {
  id: string;
  ownerNodeId: string;
  ownerNodeName: string;
  homeNodeId?: string;
  awayNodeId?: string;
  homeNodeName?: string;
  awayNodeName?: string;
  title: string;
  description: string;
  eventType: string;
  location: string;
  locationUrl?: string;
  isJoint?: boolean;
  participatingNodeIds?: string[];
  startTime: string;
  endTime: string;
  allDay: boolean;
  status: OrgEventStatus;
  locked: boolean;
  rsvpCounts: {
    YES: number;
    NO: number;
    MAYBE: number;
  };
  myRsvp: RsvpResponse;
  userRsvp?: RsvpResponse;
  metadata?: Record<string, any>;
  nodeId?: string; // from backend
  createdBy?: string; // from backend
  creatorId?: string;
  createdById?: string;
  creatorUserId?: string;
  userId?: string;
}

export interface Transaction {
  id: string;
  nodeId: string;
  nodeName?: string; // Name of the organization where the transaction occurred
  userId: string;
  userName: string;
  amount: number;
  description: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED';
  createdAt: string;
  stripePaymentIntentId?: string;
  isAnonymous?: boolean;
  onBehalfOfNotes?: string;
}

export interface CreatePaymentIntentRequest {
  amount: number;
  description: string;
  isAnonymous?: boolean;
  onBehalfOfNotes?: string;
}

export interface SplitResult {
  nodeId: string;
  nodeName: string;
  amount: number;
  nodeColor: string;
  percentage: number;
}

export type RequirementStatus = 'TODO' | 'PENDING' | 'DONE' | 'REVISION_REQUESTED';

export interface Requirement {
  id: string;
  nodeId: string;
  title: string;
  description: string;
  groupId: string;
  groupName: string;
  status: RequirementStatus;
  evidenceUrl?: string;
  evidenceNotes?: string;
  verifiedAt?: string;
  verifiedBy?: string;
  feedback?: string;
}

export interface RequirementGroup {
  id: string;
  nodeId: string;
  nodeName?: string; // Originating organization
  name: string;
  description: string;
  requirements: Requirement[];
  progress: {
    total: number;
    completed: number;
    percentage: number;
  };
}

export interface MemberRequirementSubmission {
  id: string;
  requirementId: string;
  requirementName: string;
  memberId: string;
  memberName: string;
  memberAvatar?: string;
  evidenceUrl: string;
  notes: string;
  submittedAt: string;
  status?: RequirementStatus | string;
}

export interface Goal {
  id: string;
  nodeId: string;
  title: string;
  description?: string;
  currentValue: number;
  targetValue: number;
  unit: string; // e.g., "$", "members", "points"
  deadline?: string;
  isPublic: boolean;
  parentId?: string;
  lastProgressNote?: string;
  lastUpdated?: string;
  subGoals?: Goal[];
}

export type Capability = 
  | 'create_announcement'
  | 'send_email_blast'
  | 'manage_members'
  | 'view_ledger'
  | 'configure_fees'
  | 'verify_requirement'
  | 'create_event'
  | 'create_poll'
  | 'manage_vault'
  | 'create_newsletter'
  | 'edit_newsletter'
  | 'manage_message_board'
  | 'manage_referrals'
  | 'manage_elections'
  | 'cast_vote'
  | 'create_goal'
  | 'create_requirement'
  | 'view_analytics'
  | 'view_newsletter'
  | 'view_chat'
  | 'view_calendar'
  | 'view_requirements'
  | 'view_goals'
  | 'view_discussions'
  | 'view_polls'
  | 'view_directory'
  | 'view_leadership'
  | 'view_ledger_admin'
  | 'view_personal_finances'
  | 'view_elections'
  | 'define_roles'
  | 'create_sub_orgs'
  | 'view_suborgs'
  | 'edit_settings'
  | 'view_support'
  | 'manage_support'
  | 'SUPPORT_VIEW'
  | 'SUPPORT_MANAGE'
  | 'ROLE_MANAGE'
  | 'MEMBER_MANAGE'
  | 'MEMBER_INVITE'
  | 'VAULT_UPLOAD'
  | 'VAULT_VIEW'
  | 'VAULT_MANAGE'
  | 'CONFIG_UPDATE'
  | 'COMMUNICATION_MANAGE'
  | 'GOAL_MANAGE'
  | 'GOAL_UPDATE'
  | 'EVENT_MANAGE'
  | 'REQUIREMENT_MANAGE'
  | 'EVIDENCE_VERIFY';

export interface CapabilityDefinition {
  id: Capability;
  label: string;
  description: string;
}

export interface RoleDefinition {
  id?: string;
  name: string;
  capabilities: Capability[];
  priority?: number; // Allows organizations to define their own hierarchical weight
  isLocked?: boolean; // System roles that shouldn't be deleted
  inherited?: boolean; // If this role is from a parent node
}

export enum IssueStatus {
  OPEN = 'OPEN',
  INVESTIGATING = 'INVESTIGATING',
  RESOLVED = 'RESOLVED',
  ESCALATED = 'ESCALATED',
  CLOSED = 'CLOSED'
}

export enum IssueCategory {
  TECHNICAL = 'TECHNICAL',
  COMPLIANCE = 'COMPLIANCE',
  CONDUCT = 'CONDUCT',
  BILLING = 'BILLING',
  OTHER = 'OTHER'
}

export enum IssueSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

export interface IssueUpdate {
  authorId: string;
  message: string;
  timestamp: string;
}

/** A single upward escalation move recorded on the ticket. */
export interface EscalationHop {
  fromNodeId: string;
  fromNodeName?: string;
  toNodeId: string;
  toNodeName?: string;
  escalatedBy: string;
  escalatedAt: string;
}

export interface Issue {
  id: string;
  /** Node the ticket is currently addressed to. Moves up the hierarchy on escalation. */
  nodeId: string;
  /** Node the ticket was raised from. Never changes, so the origin can keep tracking it. */
  originNodeId?: string;
  originNodeName?: string;
  /** Display name of the node the ticket is currently addressed to. */
  addressedToName?: string;
  requesterId: string;
  authorName: string;
  title: string;
  description: string;
  category: IssueCategory;
  severity: IssueSeverity;
  status: IssueStatus;
  createdAt: string;
  updatedAt: string;
  parentIssueId?: string; // If escalated from a child node
  resolutionNotes?: string;
  assignedTo?: string;
  assignedToName?: string;
  escalatedToSystem?: boolean; // True if it currently sits at the platform root
  /** Every upward escalation taken, oldest first. */
  escalationTrail?: EscalationHop[];
  /** Comments and system events, oldest first. */
  updates?: IssueUpdate[];
}

export interface NodeConfig {
  primaryColor?: string;
  secondaryColor?: string;
  logoUrl?: string;
  bannerUrl?: string;
  fontFamily?: 'sans' | 'mono' | 'display' | 'serif';
  roundedCorners?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  themeId?: 'bauhaus';
  modules?: string[];
  requiredDues?: number;
  industryCategory?: string;
  customRoles?: Record<string, string>; // Legacy mapping
  roleDefinitions?: RoleDefinition[]; // New dynamic roles
  terminology?: {
    member?: string;      // e.g. "Soldier", "Employee", "Athelete"
    childNode?: string;   // e.g. "Squad", "Team", "Office"
    parentNode?: string;  // e.g. "Platoon", "HQ", "Division"
    pulse?: string;       // e.g. "Orders", "Gazette", "Feed"
    ledger?: string;      // e.g. "Treasury", "Finances", "Bank"
    requirements?: string; // e.g. "Tasks", "Compliance", "Checklist"
    goals?: string;        // e.g. "Targets", "KPIs", "Objectives"
    discussions?: string;  // e.g. "Forum", "Board", "Talk"
    support?: string;      // e.g. "Help Desk", "Support", "Center"
  };
  marketingCopy?: {
    heroHeadline?: string;
    heroSubheadline?: string;
    heroCta?: string;
    heroDescription?: string;
    featuresHeadline?: string;
    featuresSubheadline?: string;
  };
  features?: {
    allowDirectUpstreamMessages?: boolean; // Can children message parent members?
    enforceParentTerminology?: boolean;   // Should terms cascade from parent?
    enableSeasonalAccents?: boolean;      // Should seasonal platform accents be applied?
  };
}

export interface NotificationPreference {
  type: string;
  label: string;
  app: boolean;
  email: boolean;
  sms: boolean;
  push: boolean;
  locked: string[]; // ['app', 'email', 'sms', 'push']
}

export interface PreferencesResponse {
  global: NotificationPreference[];
  nodeSpecific: { [nodeId: string]: NotificationPreference[] };
}

export interface PreferencesUpdateRequest {
  global?: NotificationPreference[];
  nodeId?: string;
  nodePreferences?: NotificationPreference[];
}

export type NodeVisibility = 'PUBLIC' | 'PROTECTED' | 'PRIVATE';

export interface Node {
  id: string;
  name: string;
  slug: string;
  description?: string;
  parentId?: string | null;
  lineageIds?: string[]; // IDs of parent nodes from top to bottom
  config: NodeConfig;
  modules: string[];
  tier: 'LOCAL' | 'FOUNDATION' | 'MOMENTUM' | 'INSTITUTIONAL';
  storageUsedBytes?: number;
  memberCount?: number;
  stripeAccountId?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  chargesEnabled?: boolean;
  subscriptionStatus?: string;
  currentPeriodEnd?: string;
  visibility: NodeVisibility;
  createdAt: string;
  updatedAt: string;
}

// Announcement replaced by PulseAlert
export type Announcement = PulseAlert;
export type Event = OrgEvent;

export type VaultItemVisibility = 'PRIVATE' | 'SHARED_UP' | 'SHARED_DOWN' | 'GLOBAL';

export interface VaultItem {
  id: string;
  nodeId: string;
  nodeName: string;
  title: string;
  description?: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  visibility: VaultItemVisibility;
  uploadedBy: string;
  uploadedByName: string;
  createdAt: string;
}

export type NewsletterStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHING' | 'PUBLISHED' | 'ARCHIVED';

export type SectionType = 'hero' | 'text' | 'image' | 'story' | 'notice' | 'button' | 'divider' | 'event_embed';

export interface BaseSection {
  id: string; // Client-generated UUID
  type: SectionType;
  orderIndex: number;
  styles?: {
    backgroundColor?: string;
    padding?: string;
    align?: 'left' | 'center' | 'right';
  };
}

export interface HeroSection extends BaseSection {
  type: 'hero';
  title: string;
  subtitle?: string;
  content?: string; // Fallback for old content field usage
  imageUrl?: string;
  ctaText?: string;
  ctaUrl?: string;
}

export interface TextSection extends BaseSection {
  type: 'text';
  title?: string;
  content?: string; // Fallback for old content field usage
  htmlContent: string;
}

export interface ImageSection extends BaseSection {
  type: 'image';
  imageUrl: string;
  caption?: string;
  content?: string; // Fallback for old content field usage
}

export interface StorySection extends BaseSection {
  type: 'story';
  title: string;
  author: string;
  content: string;
  imageUrl?: string;
}

export interface NoticeSection extends BaseSection {
  type: 'notice';
  title: string;
  content: string;
  severity: 'info' | 'warning' | 'error';
}

export interface ButtonSection extends BaseSection {
  type: 'button';
  text: string;
  url: string;
}

export interface DividerSection extends BaseSection {
  type: 'divider';
}

export interface EventEmbedSection extends BaseSection {
  type: 'event_embed';
  eventId: string;
}

export type NewsletterSection = 
  | HeroSection 
  | TextSection 
  | ImageSection 
  | StorySection 
  | NoticeSection 
  | ButtonSection 
  | DividerSection 
  | EventEmbedSection;

export interface NewsletterAudienceFilters {
  roles?: string[];
  tags?: string[];
  membershipTiers?: string[];
  includeDescendants: boolean;
}

export interface Newsletter {
  id: string;
  nodeId: string;
  nodeName?: string; // Added for display in cards
  authorUserId: string;
  authorName: string;
  title: string;
  subjectLine: string;
  previewText: string;
  content?: string; // Fallback if sections are empty
  pdfUrl?: string; // Digital download link
  issueNumber?: string;
  status: NewsletterStatus;
  audienceFilters?: NewsletterAudienceFilters;
  scheduledFor: string | null;
  publishedAt: string | null;
  thumbnailUrl: string | null;
  cascadesDown: boolean;
  sections: NewsletterSection[];
  metrics: {
    recipientsTotal: number;
    deliveredEmail: number;
    deliveredSms: number;
    deliveredPush: number;
    uniqueOpens: number;
    linkClicks: number;
    bounces: number;
  };
  createdAt: string;
  updatedAt: string;
  version: number;
}

export enum ContributionStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  USED = 'USED'
}

export interface Contribution {
  id: string;
  nodeId: string;
  authorId: string;
  authorName?: string;
  title?: string;
  content: string;
  imageUrl?: string;
  status: ContributionStatus;
  createdAt: string;
}

export interface MessageBoardTopic {
  id: string;
  nodeId: string;
  nodeName: string;
  title: string;
  content: string;
  authorUserId: string;
  authorName: string;
  authorAvatarColor?: string;
  isPrivate: boolean;
  cascadesDown: boolean;
  commentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface MessageBoardComment {
  id: string;
  topicId: string;
  parentId?: string;
  content: string;
  authorUserId: string;
  authorName: string;
  authorAvatarColor?: string;
  depth: number;
  createdAt: string;
  replies?: MessageBoardComment[];
}

export interface RelationshipType {
  type: 'GUARDIAN' | 'PARENT' | 'SPONSOR' | 'OWNER' | 'DEPENDENT' | 'PET';
  label: string;
}

export interface MemberRelationship {
  id: string;
  nodeId: string;
  dependentId: string; // The "Primary" in the user's view (Child, Pet)
  sponsorId: string; // The "Secondary" in the user's view (Parent, Owner)
  relationshipType: string;
  isFinancialSponsor: boolean; // Does the sponsor pay for the dependent?
  isCommunicationSponsor: boolean; // Does the sponsor receive notifications for the dependent?
}

export interface DirectoryMember {
  id: string;
  userId: string;
  name: string;
  avatarUrl?: string;
  email: string;
  role: string;
  nodeId: string;
  nodeName: string;
  joinedAt: string;
  skills?: string[];
  metadata?: Record<string, any>;
  memberType?: 'PRIMARY' | 'DEPENDENT' | 'SPONSOR';
  inviteLink?: string;
}

export interface DirectoryFilters {
  name?: string;
  role?: string[];
  skill?: string;
  attribute?: string;
  includeDescendants?: boolean;
}

export interface Member {
  id: string;
  nodeId: string;
  nodeName: string;
  userId: string;
  name: string;
  role: string;
  joinedAt: string;
  status: string;
  vettingLevel?: number;
}

export interface PulseAlert {
  id: string;
  nodeId: string;
  nodeName?: string;
  title?: string;
  body: string;
  type: PulseType;
  severity: PulseSeverity;
  audience: 'MEMBERS' | 'PUBLIC';
  targetRoles: string[];
  cascadesDown: boolean;
  requiresAck: boolean;
  expiresAt?: string;
  createdAt: string;
  authorUserId: string;
  authorName?: string;
  authorRole?: string;
  acknowledgedBy: string[];
  relayCount: number;
  linkUrl?: string;
  linkText?: string;
  resolved?: boolean;
  isAcknowledged?: boolean;
  isRead?: boolean;
  viewCount?: number;
}

export interface AuditLog {
  id: string;
  nodeId: string;
  userId: string;
  userName: string;
  action: string;
  resourceId?: string;
  resourceType: string;
  metadata?: Record<string, any>;
  details?: string;
  createdAt: string;
}

export interface Referral {
  id: string;
  referrerNodeId: string;
  referredNodeName?: string;
  targetNodeId?: string;
  referralCode: string;
  creditsEarned: number; // in cents
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED';
  createdAt: string;
  acceptedAt?: string;
}

export interface Election {
  id: string;
  nodeId: string;
  title: string;
  description: string;
  status: 'UPCOMING' | 'ACTIVE' | 'CONCLUDED';
  type?: 'ELECTION' | 'POLL' | string;
  startDate: string;
  endDate: string;
  roles: string[];
  allowSelfNomination: boolean;
  isAnonymous: boolean;
  totalVotes?: number;
}

export interface Candidate {
  id: string;
  electionId: string;
  userId: string;
  name: string;
  photoUrl?: string;
  role: string;
  statement: string;
  platform?: string;
  votesReceived?: number;
}

export interface NodeStatsResponse {
  total: number;
  pending: number;
  count: number;
}

export interface Credential {
  id: string;
  name: string;
  issuer: string;
  status: 'VERIFIED' | 'PENDING' | 'EXPIRED';
  expires: string;
  type: string;
}

export interface UserPassportResponse {
  status?: 'PENDING' | 'VERIFIED' | 'REJECTED' | 'UNVERIFIED' | string;
  vettingLevel: number;
  credentials: Credential[];
}

export interface SubscribeRequest {
  email: string;
  name?: string;
  tags?: string[];
}

export interface SendReferralRequest {
  email: string;
  name?: string;
}

export interface CreatePulseAlertRequest {
  title?: string;
  body: string;
  type: PulseType;
  severity: PulseSeverity;
  audience: 'MEMBERS' | 'PUBLIC';
  targetRoles: string[];
  cascadesDown: boolean;
  requiresAck: boolean;
  expiresAt?: string;
  linkUrl?: string;
  linkText?: string;
}

export interface CreateChannelRequest {
  name: string;
  channelType: ChannelType;
  allowedRoles?: string[];
  isPrivate: boolean;
}

export interface CreateElectionRequest {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  roles: string[];
  allowSelfNomination: boolean;
  isAnonymous: boolean;
}

export interface SendMessageRequest {
  body: string;
  replyToId?: string;
}

export interface CheckoutRequest {
  powerupId: string;
  amount: number;
  currency: string;
  successUrl: string;
  cancelUrl: string;
}

export interface NodeRequestDTO {
  name?: string;
  title?: string;
  description?: string;
  type?: string;
  tier?: 'LOCAL' | 'FOUNDATION' | 'MOMENTUM' | 'INSTITUTIONAL';
  visibility?: NodeVisibility;
  config?: Record<string, any>;
  branding?: Record<string, any>;
}

export interface NodeResponse {
  id?: string;
  name?: string;
  slug?: string;
  title?: string;
  description?: string;
  type?: string;
  tier?: 'LOCAL' | 'FOUNDATION' | 'MOMENTUM' | 'INSTITUTIONAL';
  parentId?: string;
  depth?: number;
  config?: Record<string, any>;
  branding?: Record<string, any>;
  modules?: Record<string, boolean>;
  stripeAccountId?: string;
  chargesEnabled?: boolean;
  visibility?: NodeVisibility;
  createdAt?: string;
}

export interface LeaderboardEntry {
  playerName: string;
  score: number;
  nodeId?: string;
  date?: string;
  depth?: string;
  level?: number;
  distance?: number;
  track?: string;
  bestLap?: number;
  totalTime?: number;
  netWorth?: number;
  metadata?: Record<string, any>;
}

export interface LeaderboardSubmission {
  playerName: string;
  score: number;
  game: string;
  nodeId?: string;
  metadata?: Record<string, any>;
  hash?: string;
}

export interface LeaderboardSubmissionResponse {
  status: string;
  id: string;
  isNewHighScore: boolean;
}

export interface AdminBroadcastParams {
  title: string;
  message: string;
  link?: string;
  type?: 'ANNOUNCEMENT' | 'ALERT' | 'EMERGENCY' | string;
}

export interface SystemAlert {
  id: string;
  nodeId: string;
  nodeName?: string;
  type?: 'ANNOUNCEMENT' | 'ALERT' | 'EMERGENCY' | string;
  title: string;
  message: string;
  link?: string;
  createdAt: string;
  recipientCount: number;
  readCount?: number;
}

export type SystemBroadcast = SystemAlert;

export interface BroadcastRecipient {
  userId: string;
  name: string;
  email?: string;
  imageUrl?: string;
  read: boolean;
  readAt?: string;
  sentAt?: string;
}

export interface DbOrphansScanResult {
  members?: number;
  invitations?: number;
  nodeRoles?: number;
  posts?: number;
  pulseAlerts?: number;
  newsletters?: number;
  channels?: number;
  messageBoardTopics?: number;
  vaultAssets?: number;
  orgEvents?: number;
  goals?: number;
  governanceRules?: number;
  elections?: number;
  contributions?: number;
  transactions?: number;
  complianceRequirements?: number;
  complianceEvidence?: number;
  supportTickets?: number;
  memberRelationships?: number;
  requirementGroups?: number;
  auditLogs?: number;
  dataExports?: number;
  totalOrphanedRecords: number;
  [key: string]: number | undefined;
}

export interface DbOrphansCleanupResult {
  members?: number;
  invitations?: number;
  nodeRoles?: number;
  posts?: number;
  pulseAlerts?: number;
  newsletters?: number;
  channels?: number;
  messageBoardTopics?: number;
  vaultAssets?: number;
  orgEvents?: number;
  goals?: number;
  governanceRules?: number;
  elections?: number;
  contributions?: number;
  transactions?: number;
  complianceRequirements?: number;
  complianceEvidence?: number;
  supportTickets?: number;
  memberRelationships?: number;
  requirementGroups?: number;
  auditLogs?: number;
  dataExports?: number;
  totalPurgedRecords: number;
  [key: string]: number | undefined;
}

export interface StorageOrphanAsset {
  id: string;
  fileName: string;
  nodeId: string;
  size: number;
  url: string;
}

export interface StorageOrphansScanResult {
  orphanedAssetsCount: number;
  orphanedBytes: number;
  unreferencedS3FilesCount: number;
  orphanedAssets: StorageOrphanAsset[];
}

export interface StorageOrphansCleanupResult {
  deletedAssetsCount: number;
  reclaimedBytes: number;
  deletedS3ObjectsCount: number;
  reconciledNodesCount: number;
}

export interface OrphanedNode {
  id: string;
  slug: string;
  name: string;
  createdAt: string;
  createdBy?: string;
  memberCount: number;
}

export interface PurgeOrphanedNodesResult {
  purgedCount: number;
  purgedNodeIds: string[];
}

export interface DatabaseIndexSyncResult {
  collectionsProcessed: number;
  totalIndexesCreated: number;
  details: Record<string, string[]>;
  completedAt: string;
}

export interface ModuleBackfillResult {
  matchedCount: number;
  modifiedCount: number;
  message: string;
}

export interface ContactInquiry {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  type: string;
  message: string;
  ipAddress?: string;
  createdAt: string;
}

export interface InvitationResponse {
  id: string;
  nodeId: string;
  nodeName?: string;
  nodeSlug?: string;
  inviterId?: string;
  email: string;
  role: string;
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
  token?: string;
  inviteLink?: string;
  expiresAt?: string;
  createdAt?: string;
}


